/**
 * Enriched recommendation API client
 *
 * Unified write path: POST /api/v1/recommendation (locationId in body + optional enrichment).
 * All read/edit/delete routes are v2. All mutating routes are authenticated
 * (Bearer + optional x-id-token), mirroring the pattern in
 * src/services/recommendationService.ts.
 *
 * Endpoints:
 *   POST   /api/v1/recommendation                        – create/upsert (bare heart or with enrichment)
 *   GET    /api/v2/recommendations/mine                  – paginated own list (all sources or exclude_auto)
 *   GET    /api/v2/recommendations/user/:userId          – another user's enriched list (viewer, same shape as mine)
 *   GET    /api/v2/recommendations/location/:locationId  – community list for a place (GT Reviews tab); section 9
 *   GET    /api/v2/recommendations/:recommendationId     – single owned row
 *   PATCH  /api/v2/recommendations/:recommendationId     – partial update
 *   DELETE /api/v2/recommendations/:recommendationId     – hard delete + token cascade
 *
 * Canonical backend contract:
 *   globe-thrivers-app-backend/docs/frontend/ENRICHED_LOCATION_RECOMMENDATIONS_API_GUIDE.md
 */

import axios, { type AxiosRequestConfig } from 'axios';
import { debug, error as logError } from '@/utils/logger';

const API_BASE_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL || 'https://app-be.globethrivers.com';

// ── Payload types ──────────────────────────────────────────────────────────

/**
 * Optional enrichment fields shared by POST /api/v1/recommendation and
 * PATCH /api/v2/recommendations/:id. Per backend `pickRecommendationFields`:
 * - Send keys you want written; omitted keys may not clear previous values on PATCH.
 * - For a full form save, send all enrichable keys each time (`notes` may be `null` to clear,
 *   `travel_tags` / `photo_urls` as arrays, `external_link` as `''` to clear when that key is sent).
 */
export interface EnrichedRecommendationPayload {
  notes?: string | null;
  travel_tags?: string[];
  photo_urls?: string[];
  external_link?: string;
}

// ── Document types ─────────────────────────────────────────────────────────

export interface EnrichedRecommendationDoc {
  _id: string;
  user_id: string;
  location_id: string;
  notes?: string;
  travel_tags?: string[];
  photo_urls?: string[];
  external_link?: string;
  created_at?: string;
  updated_at?: string;
  /**
   * Set by backend on all writes; may be absent on pre-backfill rows.
   * - 'explicit': bare heart via POST /api/v1/recommendation (no enrichment fields)
   * - 'enriched': POST /api/v1/recommendation with notes/tags/photos/link, or PATCH
   * - 'auto': created implicitly when a post containing the location was saved
   *
   * exclude_auto=true on `mine` and user_initiated_recommendation_count both align
   * on recommendation_source in ['explicit','enriched']. Rows missing this field are
   * treated as implicit (not user-initiated) until backfilled.
   */
  recommendation_source?: 'explicit' | 'auto' | 'enriched';
  /** Present when the backend tracks recommendation page views. */
  view_count?: number;
}

export interface EnrichedRecommendationLocation {
  _id: string;
  name?: string;
  description?: string;
  a3_intl_address?: string;
  address?: string;
  google_place_id?: string;
  google_types?: string[];
  gt_category?: 'eat' | 'stay' | 'do';
  category_name?: string;
  main_photo?: string;
  image_urls?: unknown[];
  photos?: Array<{ name?: string; photo_reference?: string }>;
  /** Full Google Places API document; present when the backend includes it. Used for coordinates and display metadata. */
  google_places_data?: Record<string, unknown>;
  rec_count?: number;
  rec_count_breakdown?: { post_locations?: number; explicit_recommendations?: number };
}

export interface RecommendationMetrics {
  view_count: number;
  location_saves_count: number;
  booking_clicks?: number;
  confirmed_bookings?: number;
  earnings?: number;
  added_to_trips_count?: number;
}

export interface EnrichedRecommendationItem {
  recommendation: EnrichedRecommendationDoc;
  /** Null when the location document is missing (orphan); show "Place unavailable" in UI. */
  location: EnrichedRecommendationLocation | null;
  metrics?: RecommendationMetrics;
}

// ── Pagination ─────────────────────────────────────────────────────────────

export interface ListMyRecommendationsParams {
  page?: number;
  limit?: number;
  /**
   * When true, serialized as `exclude_auto=true` on the request.
   * Filters to rows where recommendation_source is 'explicit' or 'enriched'
   * (same predicate as user_initiated_recommendation_count on the profile payload).
   * Rows with missing/null recommendation_source are excluded until backfilled.
   */
  excludeAuto?: boolean;
  /**
   * When true, serialized as `include_metrics=true`. The backend then attaches a
   * lightweight `metrics` object to each item (batch-loaded; avoids N+1 per-rec calls).
   * Only request this where the dashboard renders per-rec metrics.
   */
  includeMetrics?: boolean;
  /**
   * When true, a 401/403 from this read won't trip the global axios auth
   * interceptor's force-logout. Used by the Performance Dashboard feed so a
   * forbidden/expired list response surfaces an in-page error instead of
   * ejecting the user from the app. Not serialized into the query string.
   */
  skipAuthErrorHandling?: boolean;
}

export interface ListMyRecommendationsResponse {
  items: EnrichedRecommendationItem[];
  pagination?: {
    page: number;
    limit: number;
    /** True when there are more pages after this one */
    has_more?: boolean;
    /** Legacy alternate name — prefer has_more when present */
    has_next?: boolean;
    /**
     * Total rows matching the current filter (reflects exclude_auto when set).
     * Use for "N of M" copy or to know when pagination is complete.
     */
    total?: number;
    /** Total number of pages: Math.ceil(total / limit) */
    total_pages?: number;
    /**
     * Count of rows with recommendation_source in ['explicit','enriched'] for this user,
     * regardless of exclude_auto. Always present on GET /api/v2/recommendations/user/:userId;
     * may be absent on mine responses. Use for Saved tab badge / total.
     */
    user_initiated_total?: number;
  };
}

// ── Public (logged-out) response ───────────────────────────────────────────

/** Public GET recommender shape (backend: user_id, name, small_avatar_url). */
export interface PublicRecommendationRecommender {
  user_id?: string;
  /** Some responses may still expose Mongo _id — prefer user_id when present */
  _id?: string;
  name: string;
  small_avatar_url?: string;
  large_avatar_url?: string;
}

export interface PublicRecommendationResponse {
  recommendation: EnrichedRecommendationDoc;
  location: EnrichedRecommendationLocation | null;
  recommender: PublicRecommendationRecommender;
}

function pickStringArrayField(v: unknown): string[] {
  if (Array.isArray(v)) {
    return v.map((x) => String(x).trim()).filter(Boolean);
  }
  if (typeof v === 'string' && v.trim()) {
    return v
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function mergeTravelTagIds(rec: Record<string, unknown>): string[] {
  const fromSnake = pickStringArrayField(rec.travel_tags);
  const fromCamel = pickStringArrayField(rec.travelTags);
  const combined = [...fromSnake, ...fromCamel];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of combined) {
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

function pickExternalLinkFromRecord(rec: Record<string, unknown>): string | undefined {
  const a = rec.external_link;
  const b = rec.externalLink;
  if (typeof a === 'string' && a.trim()) return a.trim();
  if (typeof b === 'string' && b.trim()) return b.trim();
  return undefined;
}

/**
 * Normalizes GET /api/v1/public/recommendation/:id JSON so enrichments always
 * land on `recommendation.travel_tags` and `recommendation.external_link` whether
 * the API used snake_case or camelCase.
 */
export function normalizePublicRecommendationResponse(
  data: PublicRecommendationResponse,
): PublicRecommendationResponse {
  const recRecord = data.recommendation as EnrichedRecommendationDoc & Record<string, unknown>;
  const mergedTags = mergeTravelTagIds(recRecord as Record<string, unknown>);
  const mergedLink = pickExternalLinkFromRecord(recRecord as Record<string, unknown>);

  const travel_tags =
    mergedTags.length > 0 ? mergedTags : (data.recommendation.travel_tags ?? []);

  const external_link =
    mergedLink !== undefined && mergedLink !== ''
      ? mergedLink
      : (data.recommendation.external_link ?? '');

  return {
    ...data,
    recommendation: {
      ...data.recommendation,
      travel_tags,
      external_link: external_link || undefined,
    },
  };
}

/**
 * Map an authenticated owner GET (`EnrichedRecommendationItem`) into the public
 * page response shape so `/recommendation/:id` can render without a share token.
 */
export function ownerRecommendationToPublicResponse(
  item: EnrichedRecommendationItem,
  recommender: PublicRecommendationRecommender,
): PublicRecommendationResponse {
  return normalizePublicRecommendationResponse({
    recommendation: item.recommendation,
    location: item.location,
    recommender,
  });
}

/** Minimal bundle shape for merging enrichments into a thin public recommendation payload. */
export interface RecommendationShareBundleLike {
  recommendation?: unknown;
  location?: unknown;
}

/**
 * When GET /api/v1/public/recommendation returns a thin projection, merge travel_tags,
 * external_link, photo_urls, notes, and location from recommendation-with-sharer bundle.
 */
export function mergeThinPublicRecommendationWithBundle(
  thin: PublicRecommendationResponse,
  bundle: RecommendationShareBundleLike | null,
): PublicRecommendationResponse {
  if (!bundle?.recommendation || typeof bundle.recommendation !== 'object') {
    return normalizePublicRecommendationResponse(thin);
  }
  const b = bundle.recommendation as Record<string, unknown>;
  const t = thin.recommendation as EnrichedRecommendationDoc & Record<string, unknown>;

  const thinTags = mergeTravelTagIds(t as Record<string, unknown>);
  const bundleTags = mergeTravelTagIds(b);
  const travel_tags = thinTags.length > 0 ? thinTags : bundleTags.length > 0 ? bundleTags : (t.travel_tags ?? []);

  const thinLink = pickExternalLinkFromRecord(t as Record<string, unknown>);
  const bundleLink = pickExternalLinkFromRecord(b);
  const external_link =
    thinLink && thinLink !== ''
      ? thinLink
      : bundleLink && bundleLink !== ''
        ? bundleLink
        : t.external_link;

  const thinPhotos = Array.isArray(t.photo_urls) ? t.photo_urls : [];
  const bundlePhotosRaw = b.photo_urls ?? b.photoUrls;
  const bundlePhotos = Array.isArray(bundlePhotosRaw)
    ? (bundlePhotosRaw as unknown[]).filter((u): u is string => typeof u === 'string')
    : [];
  const photo_urls =
    thinPhotos.length > 0 ? thinPhotos : bundlePhotos.length > 0 ? bundlePhotos : t.photo_urls;

  const notesThin = typeof t.notes === 'string' ? t.notes.trim() : '';
  const notesBundle = typeof b.notes === 'string' ? (b.notes as string).trim() : '';
  const notes = notesThin || notesBundle || t.notes;

  const location =
    thin.location ?? ((bundle.location as EnrichedRecommendationLocation | null | undefined) ?? null);

  return normalizePublicRecommendationResponse({
    ...thin,
    location,
    recommendation: {
      ...t,
      notes: notes || undefined,
      travel_tags,
      external_link: external_link?.trim() || undefined,
      photo_urls,
    },
  });
}

// ── Auth helpers ───────────────────────────────────────────────────────────

function getAuthHeaders(): Record<string, string> {
  const rawAccessToken =
    typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
  if (!rawAccessToken) {
    throw new Error('Authentication token not found. User must be logged in.');
  }
  const accessToken = rawAccessToken.startsWith('Bearer ')
    ? rawAccessToken.substring(7)
    : rawAccessToken;

  const headers: Record<string, string> = {
    Authorization: accessToken,
    'Content-Type': 'application/json',
  };

  const rawIdToken =
    typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
  if (rawIdToken) {
    const idToken = rawIdToken.startsWith('Bearer ')
      ? rawIdToken.substring(7)
      : rawIdToken;
    headers['x-id-token'] = idToken;
  }

  return headers;
}

/** Auth headers when signed in, none otherwise (for endpoints that also serve logged-out users). */
function getOptionalAuthHeaders(): Record<string, string> {
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');
  return hasToken ? getAuthHeaders() : {};
}

// ── API methods ────────────────────────────────────────────────────────────

/**
 * Create or update a recommendation for the current user and a given location.
 * Idempotent on the (user_id, location_id) pair. Pass optional enrichment fields
 * for a full-form save; omit them for a bare heart (recommendation_source → 'explicit').
 * POST /api/v1/recommendation
 */
export async function createRecommendation(
  locationId: string,
  payload?: EnrichedRecommendationPayload,
): Promise<EnrichedRecommendationItem> {
  const endpoint = `${API_BASE_URL}/api/v1/recommendation`;
  debug('[enrichedRecommendation] Creating recommendation', { locationId });
  try {
    const headers = getAuthHeaders();
    const body = { locationId, ...payload };
    const response = await axios.post<EnrichedRecommendationItem>(endpoint, body, { headers });
    debug('[enrichedRecommendation] Create success', { locationId, id: response.data.recommendation._id });
    return response.data;
  } catch (err) {
    logError('[enrichedRecommendation] Create failed', { locationId, error: err });
    throw err;
  }
}

/** Shared query-param builder for both `mine` and `user/:userId` list endpoints. */
function buildEnrichedRecommendationsListQueryParams(
  params: ListMyRecommendationsParams,
): Record<string, unknown> {
  const q: Record<string, unknown> = {
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  };
  if (params.excludeAuto === true) {
    q.exclude_auto = 'true';
  }
  if (params.includeMetrics === true) {
    q.include_metrics = 'true';
  }
  return q;
}

/**
 * Fetch the current user's paginated enriched recommendations.
 * GET /api/v2/recommendations/mine
 */
export async function listMyRecommendations(
  params: ListMyRecommendationsParams = {},
): Promise<ListMyRecommendationsResponse> {
  const endpoint = `${API_BASE_URL}/api/v2/recommendations/mine`;
  debug('[enrichedRecommendation] Listing my recommendations', params);
  try {
    const headers = getAuthHeaders();
    const config: AxiosRequestConfig & { skipAuthErrorHandling?: boolean } = {
      headers,
      params: buildEnrichedRecommendationsListQueryParams(params),
    };
    if (params.skipAuthErrorHandling) {
      config.skipAuthErrorHandling = true;
    }
    const response = await axios.get<ListMyRecommendationsResponse>(endpoint, config);
    debug('[enrichedRecommendation] List success', {
      count: response.data.items?.length ?? 0,
      total: response.data.pagination?.total,
    });
    return response.data;
  } catch (err) {
    logError('[enrichedRecommendation] List failed', { error: err });
    throw err;
  }
}

/**
 * Fetch another user's enriched recommendations as a viewer.
 * Returns the same { items, pagination } shape as `mine`; pagination always includes
 * user_initiated_total (count of explicit|enriched rows regardless of exclude_auto).
 * GET /api/v2/recommendations/user/:userId
 */
export async function listUserRecommendationsForViewer(
  userId: string,
  params: ListMyRecommendationsParams = {},
): Promise<ListMyRecommendationsResponse> {
  const endpoint = `${API_BASE_URL}/api/v2/recommendations/user/${encodeURIComponent(userId)}`;
  debug('[enrichedRecommendation] Listing recommendations for user', { userId, params });
  try {
    const headers = getAuthHeaders();
    const response = await axios.get<ListMyRecommendationsResponse>(endpoint, {
      headers,
      params: buildEnrichedRecommendationsListQueryParams(params),
    });
    debug('[enrichedRecommendation] Viewer list success', {
      userId,
      count: response.data.items?.length ?? 0,
      total: response.data.pagination?.total,
      user_initiated_total: response.data.pagination?.user_initiated_total,
    });
    return response.data;
  } catch (err) {
    logError('[enrichedRecommendation] Viewer list failed', { userId, error: err });
    throw err;
  }
}

/**
 * Fetch a single owned recommendation by its _id (for edit pre-fill).
 * GET /api/v2/recommendations/:recommendationId
 */
export async function getRecommendation(
  recommendationId: string,
): Promise<EnrichedRecommendationItem> {
  const endpoint = `${API_BASE_URL}/api/v2/recommendations/${encodeURIComponent(recommendationId)}`;
  debug('[enrichedRecommendation] Getting recommendation', { recommendationId });
  try {
    const headers = getAuthHeaders();
    const response = await axios.get<EnrichedRecommendationItem>(endpoint, { headers });
    return response.data;
  } catch (err) {
    logError('[enrichedRecommendation] Get failed', { recommendationId, error: err });
    throw err;
  }
}

/**
 * Partially update an existing recommendation.
 * PATCH /api/v2/recommendations/:recommendationId
 */
export async function patchRecommendation(
  recommendationId: string,
  partial: Partial<EnrichedRecommendationPayload>,
): Promise<EnrichedRecommendationDoc> {
  const endpoint = `${API_BASE_URL}/api/v2/recommendations/${encodeURIComponent(recommendationId)}`;
  debug('[enrichedRecommendation] Patching recommendation', { recommendationId });
  try {
    const headers = getAuthHeaders();
    const response = await axios.patch<EnrichedRecommendationDoc>(endpoint, partial, { headers });
    return response.data;
  } catch (err) {
    logError('[enrichedRecommendation] Patch failed', { recommendationId, error: err });
    throw err;
  }
}

/**
 * Find the current user's existing recommendation for a given location, if any.
 * There is no server-side filter by location_id on the mine endpoint, so we
 * fetch up to 200 rows (covers all but extreme power users) and match client-side.
 * Returns null when no matching row is found or the user is unauthenticated.
 * GET /api/v2/recommendations/mine
 */
export async function getMyRecommendationForLocation(
  locationId: string,
): Promise<EnrichedRecommendationDoc | null> {
  if (!locationId) return null;
  try {
    const response = await listMyRecommendations({ page: 1, limit: 200 });
    const match = response.items.find(
      (item) => item.recommendation.location_id === locationId,
    );
    return match?.recommendation ?? null;
  } catch (err) {
    // Silently return null so the enriched modal still opens (in create mode)
    debug('[enrichedRecommendation] getMyRecommendationForLocation: fetch failed', { locationId, err });
    return null;
  }
}

/**
 * Hard-delete a recommendation (owner only). Backend cascades share token
 * deactivation for all tokens with resource_type === 'recommendation' and
 * resource_id === this doc's _id.
 * DELETE /api/v2/recommendations/:recommendationId
 */
export async function deleteRecommendation(recommendationId: string): Promise<void> {
  const endpoint = `${API_BASE_URL}/api/v2/recommendations/${encodeURIComponent(recommendationId)}`;
  debug('[enrichedRecommendation] Deleting recommendation', { recommendationId });
  try {
    const headers = getAuthHeaders();
    await axios.delete(endpoint, { headers });
    debug('[enrichedRecommendation] Delete success', { recommendationId });
  } catch (err) {
    logError('[enrichedRecommendation] Delete failed', { recommendationId, error: err });
    throw err;
  }
}

// ── Community / GT Reviews list by location ────────────────────────────────

/** One row from GET /api/v2/recommendations/location/:locationId. */
export interface LocationRecommendationItem {
  recommendation: EnrichedRecommendationDoc;
  /**
   * Null when the recommender profile can no longer be resolved.
   * Render as "Unknown user" — do not assume a follow-up fetch will succeed.
   */
  recommender: PublicRecommendationRecommender | null;
}

export interface ListLocationRecommendationsParams {
  page?: number;
  limit?: number;
  /**
   * When true, serialized as `exclude_auto=true`.
   * Filters to recommendation_source in 'explicit' | 'enriched' (user-initiated only).
   * Default for the GT Reviews tab per backend guidance.
   */
  excludeAuto?: boolean;
}

export interface ListLocationRecommendationsResponse {
  items: LocationRecommendationItem[];
  pagination: {
    page: number;
    limit: number;
    /** True when additional pages exist. Use for "Load more". */
    has_more: boolean;
    total: number;
    total_pages: number;
    /**
     * Count of explicit + enriched rows for this location regardless of exclude_auto.
     * Use for "GT Reviews (N)" copy without a second request.
     */
    user_initiated_total: number;
  };
}

/**
 * Fetch paginated community recommendations for a location (GT Reviews tab).
 * Authenticated only — call only when the user is logged in.
 * GET /api/v2/recommendations/location/:locationId
 * Full contract: backend docs/frontend/ENRICHED_LOCATION_RECOMMENDATIONS_API_GUIDE.md section 9.
 */
export async function listRecommendationsForLocation(
  locationId: string,
  params: ListLocationRecommendationsParams = {},
): Promise<ListLocationRecommendationsResponse> {
  const endpoint = `${API_BASE_URL}/api/v2/recommendations/location/${encodeURIComponent(locationId)}`;
  debug('[enrichedRecommendation] Listing recommendations for location', { locationId, params });
  const q: Record<string, unknown> = {
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  };
  if (params.excludeAuto === true) {
    q.exclude_auto = 'true';
  }
  try {
    const headers = getOptionalAuthHeaders();
    const response = await axios.get<ListLocationRecommendationsResponse>(endpoint, {
      headers,
      params: q,
    });
    debug('[enrichedRecommendation] Location list success', {
      locationId,
      count: response.data.items?.length ?? 0,
      user_initiated_total: response.data.pagination?.user_initiated_total,
    });
    return response.data;
  } catch (err) {
    logError('[enrichedRecommendation] Location list failed', { locationId, error: err });
    throw err;
  }
}
